import { State, useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import { useEffect, useMemo } from 'react';
import { isBeforeTimestamp } from '@bonanza-social/toolkit/utils';
import { HistoryType } from '../state/shared/history.types';
import { historyStateMap } from '../state/shared/history.content';

function useHistory(cutoffTimestamp: string, type: HistoryType) {
  const dispatch = useDispatch();
  const hasNextPage = useSelector(historyStateMap[type].hasNextPageSelector);
  const pageNumber = useSelector(historyStateMap[type].pageNumberSelector);
  const items = useSelector((state: State) =>
    historyStateMap[type].itemsSelector(state, cutoffTimestamp),
  );

  const lastTimestamp = useSelector(
    historyStateMap[type].lastTimestampSelector,
  );
  const dataState = useSelector(historyStateMap[type].dataStateSelector);
  const fetchHistory = historyStateMap[type].fetchHistory;
  const resetLastTimestamp = historyStateMap[type].resetLastTimestamp;
  const isFetching = useMemo(
    () => Boolean(dataState.match('idle|pending')),
    [dataState],
  );

  const hasPaymentMethodsAwaitingDocumentReview = useSelector(
    historyStateMap[type]?.hasPaymentMethodsAwaitingDocumentReview ||
      (() => undefined),
  );
  const hasPaymentMethodsAwaitingDocumentVerification = useSelector(
    historyStateMap[type]?.hasPaymentMethodsAwaitingDocumentVerification ||
      (() => undefined),
  );

  useEffect(() => {
    //  Refetch if the user chooses an older timestamp than the last fetch or first open history
    if (!lastTimestamp || isBeforeTimestamp(cutoffTimestamp, lastTimestamp)) {
      dispatch(
        fetchHistory({
          startDate: cutoffTimestamp,
          replace: true,
        }),
      );
    }
  }, [cutoffTimestamp, lastTimestamp, dispatch, fetchHistory]);

  const returnObj = useMemo(
    () => ({
      items,
      hasNextPage,
      pageNumber,
      lastTimestamp,
      isFetching,
      fetchHistory,
      dataState,
      resetLastTimestamp,
      hasPaymentMethodsAwaitingDocumentReview,
      hasPaymentMethodsAwaitingDocumentVerification,
    }),
    [
      items,
      hasNextPage,
      pageNumber,
      lastTimestamp,
      isFetching,
      fetchHistory,
      dataState,
      resetLastTimestamp,
      hasPaymentMethodsAwaitingDocumentReview,
      hasPaymentMethodsAwaitingDocumentVerification,
    ],
  );

  return returnObj;
}

export default useHistory;
